/**
 * RegistroDetailDialog — scheda dettaglio read-only per i registri HACCP
 * (stile Ricevimenti): header navy con stato, griglia info, foto/allegati
 * visibili inline, note, azioni footer (Modifica/Chiudi).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, StickyNote, FileText, Paperclip, Pencil } from "lucide-react";

const TONE_DOT = { ok: "bg-emerald-400", bad: "bg-rose-400", warn: "bg-amber-400", neutral: "bg-slate-300" };
const isImage = (f) => (f?.content_type || "").startsWith("image/");

function Cell({ label, value, tone, mono }) {
  const toneCls = tone === "bad" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <p className={`mt-1 text-sm font-semibold break-words ${toneCls} ${mono ? "font-mono" : ""}`}>{value ?? "—"}</p>
    </div>
  );
}

export default function RegistroDetailDialog({
  open, onClose, statusLabel, statusTone = "neutral", title, subtitle,
  cells = [], photoUrl, photoLabel = "Foto allegata", attachments, note, onEdit,
}) {
  const [files, setFiles] = useState([]);
  const base = api.defaults.baseURL;

  useEffect(() => {
    if (open && attachments?.recordId) {
      api.get(`/files?kind=${attachments.kind}&record_id=${attachments.recordId}`)
        .then(({ data }) => setFiles(data || []))
        .catch(() => setFiles([]));
    } else {
      setFiles([]);
    }
  }, [open, attachments?.kind, attachments?.recordId]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="registro-detail-dialog">
        <DialogTitle className="sr-only">Dettaglio {title}</DialogTitle>
        <DialogDescription className="sr-only">Scheda di dettaglio del record registrato.</DialogDescription>

        <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3.5 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {statusLabel && (
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${TONE_DOT[statusTone] || TONE_DOT.neutral}`} aria-hidden />
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold truncate">{statusLabel}</p>
              </div>
            )}
            <h3 className="font-extrabold text-base sm:text-lg leading-tight mt-0.5 break-words" data-testid="registro-detail-title">{title || "—"}</h3>
            {subtitle && <p className="text-[11px] text-white/70 mt-0.5 line-clamp-2">{subtitle}</p>}
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center text-white shrink-0"
            aria-label="Chiudi" data-testid="registro-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {cells.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {cells.filter(Boolean).map((c, i) => <Cell key={i} {...c} />)}
            </div>
          )}

          {photoUrl && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-2">
                <Paperclip className="w-3 h-3" /> {photoLabel}
              </p>
              <a href={photoUrl} target="_blank" rel="noreferrer" data-testid="registro-detail-photo-link">
                <img src={photoUrl} alt={photoLabel} loading="lazy"
                  className="w-full max-h-64 object-contain rounded-lg border border-slate-200 bg-white"
                  data-testid="registro-detail-photo" />
              </a>
            </div>
          )}

          {files.length > 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Allegati ({files.length})
              </p>
              {files.map((f) => (
                <a key={f.id} href={`${base}/files/${f.id}/download`} target="_blank" rel="noreferrer"
                  className="block" data-testid={`registro-detail-file-${f.id}`}>
                  {isImage(f) ? (
                    <img src={`${base}/files/${f.id}/download`} alt={f.original_filename || "allegato"} loading="lazy"
                      className="w-full max-h-64 object-contain rounded-lg border border-slate-200 bg-white" />
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-blue-700 font-semibold hover:underline">
                      <FileText className="w-3.5 h-3.5" /> {f.original_filename || "Apri allegato"}
                    </span>
                  )}
                </a>
              ))}
            </div>
          )}

          {(note || "").trim() && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-1 mb-1">
                <StickyNote className="w-3 h-3" /> Note
              </p>
              <p className="text-[12px] text-amber-900 whitespace-pre-wrap">{note}</p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-3 py-2.5 bg-slate-50 flex items-center gap-2">
          {onEdit && (
            <button onClick={onEdit}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-[12px] font-semibold hover:bg-slate-100"
              data-testid="registro-detail-edit-btn">
              <Pencil className="w-3.5 h-3.5" /> Modifica
            </button>
          )}
          <button onClick={onClose}
            className="ml-auto px-4 py-2 rounded-lg bg-[#13294B] text-white text-[12px] font-bold hover:bg-[#1E3A5F]"
            data-testid="registro-detail-close-btn">
            Chiudi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
